import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useConsent } from "@/lib/consent";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-mono text-base text-white uppercase tracking-wider mb-4 border-b border-[#2A2A2A] pb-3">
        {title}
      </h2>
      <div className="text-[#BEB3A6] font-sans leading-relaxed space-y-3 text-[0.95rem]">
        {children}
      </div>
    </section>
  );
}

function CookieRow({ name, provider, purpose, duration, type }) {
  return (
    <tr className="border-b border-[#1E1E1E]">
      <td className="py-3 pr-4 font-mono text-xs text-[#CC1A1A] align-top">{name}</td>
      <td className="py-3 pr-4 text-sm align-top">{provider}</td>
      <td className="py-3 pr-4 text-sm align-top">{purpose}</td>
      <td className="py-3 pr-4 text-sm align-top whitespace-nowrap">{duration}</td>
      <td className="py-3 text-sm align-top">{type}</td>
    </tr>
  );
}

export default function CookiesPage() {
  const { accept: handleOptIn, decline: handleOptOut } = useConsent();

  return (
    <div className="bg-[#111111] min-h-screen">
      <Helmet>
        <title>Cookie Policy – Restricted Deals</title>
        <meta name="description" content="How Restricted Deals uses cookies and how to manage your preferences." />
        <link rel="canonical" href="https://restricteddeals.com/cookies" />
      </Helmet>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="eyebrow eyebrow-red mb-4">// Legal</p>
        <h1 className="font-mono text-4xl md:text-5xl text-white uppercase tracking-tight mb-4">
          Cookie Policy
        </h1>
        <p className="text-[#555] font-mono text-xs tracking-widest uppercase mb-14">
          Last updated: May 2026 · Restricted Deals · Netherlands
        </p>

        <Section title="1. What are cookies?">
          <p>
            Cookies are small text files placed in your browser when you visit a website. We use
            the minimum number of cookies needed to operate this site and measure its performance.
            We use analytics cookies (PostHog) and advertising cookies (Google AdSense). We do not engage in cross-site tracking beyond these providers.
          </p>
        </Section>

        <Section title="2. Cookies we set">
          <div className="overflow-x-auto mt-2 -mx-1">
            <table className="w-full text-[#BEB3A6] text-left">
              <thead>
                <tr className="border-b border-[#2A2A2A] text-[10px] font-mono uppercase tracking-widest text-[#555]">
                  <th className="pb-3 pr-4">Name</th>
                  <th className="pb-3 pr-4">Provider</th>
                  <th className="pb-3 pr-4">Purpose</th>
                  <th className="pb-3 pr-4">Duration</th>
                  <th className="pb-3">Type</th>
                </tr>
              </thead>
              <tbody>
                <CookieRow
                  name="ph_*"
                  provider="PostHog"
                  purpose="Anonymous analytics — page views, session counts"
                  duration="1 year"
                  type="Analytics"
                />
                <CookieRow
                  name="access_token"
                  provider="Restricted Deals"
                  purpose="HttpOnly admin session cookie (only set for site administrators; not readable by JavaScript)"
                  duration="Up to 30 days"
                  type="Strictly necessary"
                />
                <CookieRow
                  name="cookie-consent"
                  provider="Restricted Deals"
                  purpose="Stores your cookie preference (localStorage)"
                  duration="1 year"
                  type="Strictly necessary"
                />
                <CookieRow
                  name="__gads, __gpi, ANID"
                  provider="Google AdSense"
                  purpose="Serve and measure ads; frequency capping and fraud prevention"
                  duration="Up to 13 months"
                  type="Advertising"
                />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="3. Advertising — Google AdSense">
          <p>
            We use <strong className="text-white">Google AdSense</strong> to display ads that help
            fund the site. AdSense sets advertising cookies to serve relevant ads and measure their
            performance. These cookies are only loaded after you accept cookies. Google processes
            data under its own privacy policy. You can opt out of personalised ads at{" "}
            <a href="https://adssettings.google.com" className="text-[#CC1A1A] hover:underline" target="_blank" rel="noopener noreferrer">
              adssettings.google.com
            </a>.
          </p>
        </Section>

        <Section title="4. Analytics — PostHog">
          <p>
            We use <strong className="text-white">PostHog</strong> to count page views and
            understand which deals attract the most interest. It is configured with{" "}
            <code className="text-[#CC1A1A] text-sm">person_profiles: "identified_only"</code> —
            no personal profile is created for regular visitors. PostHog processes data on US
            infrastructure under the EU–US Data Privacy Framework. Data is never sold or shared
            with advertisers.
          </p>
        </Section>

        <Section title="5. Your choices">
          <p>
            Analytics and advertising cookies require your consent. Strictly necessary cookies cannot be disabled
            — they are required for the site to function correctly.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={handleOptIn} className="btn-rd !py-2 !px-5 !text-xs">
              Accept cookies →
            </button>
            <button
              onClick={handleOptOut}
              className="border border-[#2A2A2A] text-[#9A8F83] hover:text-white hover:border-[#555] px-5 py-2 text-xs font-mono uppercase tracking-wider transition-colors"
            >
              Decline cookies
            </button>
          </div>
          <p className="mt-4 text-xs text-[#555] font-mono uppercase tracking-wider">
            Your preference is saved immediately and persists across visits.
          </p>
        </Section>

        <Section title="6. Browser controls">
          <p>
            You can also block or delete cookies through your browser settings. Note that blocking
            strictly necessary cookies may break site functionality.
          </p>
          <p>
            Guides:{" "}
            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] hover:underline">Chrome</a>
            {" · "}
            <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] hover:underline">Firefox</a>
            {" · "}
            <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] hover:underline">Safari</a>
          </p>
        </Section>

        <Section title="7. Changes">
          <p>
            We will update this page if our cookie usage changes and revise the date above.
          </p>
        </Section>

        <div className="hr-dot my-10" />
        <div className="flex gap-6 text-[11px] font-mono tracking-widest uppercase text-[#555]">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
